import { supabase } from '@/lib/supabase';
import { porIdiomaDelPais, forcedSiteLanguage } from '@/lib/country';

/**
 * Textos de los correos de esta página. Los recibe la AUTOMOTORA (o el
 * vendedor): van en el idioma del país del tenant, que llega por parámetro
 * (`country`, de clients.country). `ES` son los literales de siempre, carácter
 * por carácter: un tenant chileno o sin país recibe exactamente el mismo HTML.
 */
const TXT_CORREO_ES = {
  lang: 'es',
  clientInfo: 'Información del Cliente',
  name: 'Nombre:',
  email: 'Email:',
  phone: 'Teléfono:',
  brand: 'Marca:',
  model: 'Modelo:',
  year: 'Año:',
  cta: 'Ver detalles en el Portal',
  footer1: 'Este es un email automático generado por',
  footer2: 'Por favor no responda a este mensaje.',
  notSpecified: 'No especificado',
  // Cita agendada
  apptTitle: 'Confirmación de Cita',
  apptH1: '¡Cita Agendada!',
  apptSubtitle: 'Tu visita ha sido confirmada',
  // Ticket #228: los mismos tres textos cuando la visita todavía NO está
  // confirmada porque la automotora tiene que aceptarla. Decirle "¡Cita
  // Agendada! Tu visita ha sido confirmada" a quien está esperando sería
  // prometer en nombre de la automotora.
  apptTitlePending: 'Solicitud de Cita',
  apptH1Pending: '¡Solicitud enviada!',
  apptSubtitlePending: 'La automotora va a confirmar tu visita',
  apptDetails: 'Detalles de la Cita',
  apptDate: '📅 Fecha:',
  apptTime: '🕐 Hora:',
  apptBranch: '📍 Sucursal:',
  apptVehicle: 'Vehículo de Interés',
  apptNotes: 'Notas del Cliente',
  // Cita cancelada
  cancelTitle: 'Cita Cancelada',
  cancelH1: 'Cita Cancelada',
  cancelSubtitle: 'La siguiente cita ha sido cancelada',
  cancelDetails: 'Detalles de la Cita Cancelada',
  cancelVehicle: 'Vehículo',
  cancelReason: 'Motivo de Cancelación',
  // Lead de vehículo
  leadTypes: {
    'buy-direct': 'Venta de Vehículo',
    'buy-consignment': 'Consignación',
    'search-request': 'Búsqueda de Vehículo',
    'sell-vehicle': 'Compra de Vehículo',
  } as Record<string, string>,
  leadTitle: 'Nuevo Lead de',
  leadH1: 'Nuevo Lead:',
  vehicleDetails: 'Detalles del Vehículo',
  mileage: 'Kilometraje:',
  condition: 'Condición:',
  price: 'Precio:',
  customerMessage: 'Mensaje del cliente',
  // Formulario configurable
  newRequest: 'Nueva solicitud',
};
const TXT_CORREO_PT: typeof TXT_CORREO_ES = {
  lang: 'pt-BR',
  clientInfo: 'Informações do cliente',
  name: 'Nome:',
  email: 'E-mail:',
  phone: 'Telefone:',
  brand: 'Marca:',
  model: 'Modelo:',
  year: 'Ano:',
  cta: 'Ver detalhes no Portal',
  footer1: 'Este é um e-mail automático gerado pela',
  footer2: 'Por favor, não responda a esta mensagem.',
  notSpecified: 'Não informado',
  apptTitle: 'Confirmação de visita',
  apptH1: 'Visita agendada!',
  apptSubtitle: 'Sua visita foi confirmada',
  apptTitlePending: 'Solicitação de visita',
  apptH1Pending: 'Solicitação enviada!',
  apptSubtitlePending: 'A loja vai confirmar sua visita',
  apptDetails: 'Detalhes da visita',
  apptDate: '📅 Data:',
  apptTime: '🕐 Horário:',
  apptBranch: '📍 Unidade:',
  apptVehicle: 'Veículo de interesse',
  apptNotes: 'Observações do cliente',
  cancelTitle: 'Visita cancelada',
  cancelH1: 'Visita cancelada',
  cancelSubtitle: 'A visita abaixo foi cancelada',
  cancelDetails: 'Detalhes da visita cancelada',
  cancelVehicle: 'Veículo',
  cancelReason: 'Motivo do cancelamento',
  leadTypes: {
    'buy-direct': 'Venda de veículo',
    'buy-consignment': 'Consignação',
    'search-request': 'Busca de veículo',
    'sell-vehicle': 'Compra de veículo',
  },
  leadTitle: 'Novo lead de',
  leadH1: 'Novo lead:',
  vehicleDetails: 'Detalhes do veículo',
  mileage: 'Quilometragem:',
  condition: 'Condição:',
  price: 'Preço:',
  customerMessage: 'Mensagem do cliente',
  newRequest: 'Nova solicitação',
};
/** EE.UU. (por país del TENANT, nunca por el idioma del visitante). */
const TXT_CORREO_EN: typeof TXT_CORREO_ES = {
  lang: 'en-US',
  clientInfo: 'Customer information',
  name: 'Name:',
  email: 'Email:',
  phone: 'Phone:',
  brand: 'Make:',
  model: 'Model:',
  year: 'Year:',
  cta: 'View details in the Portal',
  footer1: 'This is an automated email sent by',
  footer2: 'Please do not reply to this message.',
  notSpecified: 'Not provided',
  apptTitle: 'Appointment confirmation',
  apptH1: 'Appointment booked!',
  apptSubtitle: 'Your visit has been confirmed',
  apptTitlePending: 'Appointment request',
  apptH1Pending: 'Request sent!',
  apptSubtitlePending: 'The dealership will confirm your visit',
  apptDetails: 'Appointment details',
  apptDate: '📅 Date:',
  apptTime: '🕐 Time:',
  apptBranch: '📍 Location:',
  apptVehicle: 'Vehicle of interest',
  apptNotes: 'Customer notes',
  cancelTitle: 'Appointment canceled',
  cancelH1: 'Appointment canceled',
  cancelSubtitle: 'The following appointment has been canceled',
  cancelDetails: 'Canceled appointment details',
  cancelVehicle: 'Vehicle',
  cancelReason: 'Cancellation reason',
  leadTypes: {
    'buy-direct': 'Vehicle sale',
    'buy-consignment': 'Consignment',
    'search-request': 'Vehicle search',
    'sell-vehicle': 'Vehicle purchase',
  },
  leadTitle: 'New lead:',
  leadH1: 'New lead:',
  vehicleDetails: 'Vehicle details',
  mileage: 'Mileage:',
  condition: 'Condition:',
  price: 'Price:',
  customerMessage: 'Customer message',
  newRequest: 'New request',
};

/** Textos del correo en el idioma del país del TENANT: pt (BR), en (US), es (el resto, como siempre). */
const txtCorreo = (country?: string | null) =>
  porIdiomaDelPais(country, { es: TXT_CORREO_ES, pt: TXT_CORREO_PT, en: TXT_CORREO_EN });

/**
 * EE.UU.: un kilometraje que no trae ningún número ("Not provided") no lleva
 * " km" pegado ("Not provided km"). El resto de los países, lo de siempre.
 */
const sinUnidadUsa = (kilometraje: string, country?: string | null): boolean =>
  forcedSiteLanguage(country) === 'en' && !/[0-9]/.test(kilometraje);

interface EmailOptions {
  to: string[];
  subject: string;
  content: string;
  from?: string;
  template_id?: string;
  template_data?: Record<string, any>;
}

/**
 * Método de respaldo que usa fetch directo para enviar email
 * en caso que el SDK de Supabase falle
 */
async function sendEmailFallback(options: EmailOptions): Promise<void> {
  const response = await fetch(
    'https://miuiujntdjrjhhcysiba.supabase.co/functions/v1/send-email',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({
        to: options.to,
        subject: options.subject,
        content: options.content,
        from: options.from,
        template_id: options.template_id,
        template_data: options.template_data,
      }),
    }
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Error al enviar el email');
  }
}

/**
 * Envía un email utilizando la función de Supabase
 * @param options Opciones del email a enviar
 * @returns Resultado de la operación
 */
export async function sendEmail(
  options: EmailOptions
): Promise<{ success: boolean; error?: string }> {
  try {
    // Usar el SDK de Supabase en lugar de fetch directo
    const { error } = await supabase.functions.invoke('send-email', {
      body: {
        to: options.to,
        subject: options.subject,
        content: options.content,
        from: options.from,
        template_id: options.template_id,
        template_data: options.template_data,
      },
    });

    if (error) {
      console.warn('Error using Supabase SDK, trying fallback method:', error);
      // Si falla el SDK, intentar con el método de fetch directo
      await sendEmailFallback(options);
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error sending email:', error);
    return {
      success: false,
      error: error.message || 'Error desconocido al enviar el email',
    };
  }
}

/**
 * Crea una plantilla HTML para confirmación de cita
 */
export function createAppointmentEmailTemplate({
  customerName,
  customerEmail,
  customerPhone,
  appointmentDate,
  appointmentTime,
  dealershipAddress,
  vehicleDetails,
  notes,
  country,
  pendingApproval,
}: {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  appointmentDate: string;
  appointmentTime: string;
  dealershipAddress: string;
  vehicleDetails: {
    brand: string;
    model: string;
    year: string;
  };
  notes?: string;
  /** País del tenant (clients.country). 'BR' → correo en portugués; el resto, el de siempre. */
  country?: string | null;
  /**
   * Ticket #228. La cita quedó esperando que la automotora la acepte
   * (`status = 'pending'`): cambian el título y el encabezado, no el cuerpo.
   *
   * El detalle —fecha, hora, sucursal, vehículo, notas— es el mismo y sirve
   * igual: es lo que la persona PIDIÓ. Lo que no puede quedar es el "tu visita
   * ha sido confirmada" del encabezado.
   *
   * Opcional y por defecto falso: así el correo de siempre no cambia en una
   * sola letra para las automotoras que no encendieron esto.
   */
  pendingApproval?: boolean;
}): string {
  const L = txtCorreo(country);
  const titulo    = pendingApproval ? L.apptTitlePending    : L.apptTitle;
  const encabezado = pendingApproval ? L.apptH1Pending      : L.apptH1;
  const bajada    = pendingApproval ? L.apptSubtitlePending : L.apptSubtitle;
  return `
    <!DOCTYPE html>
    <html lang="${L.lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${titulo}</title>
    </head>
    <body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 650px; margin: 0 auto; padding: 20px; color: #333; background: #f8f9fa;">
      <div style="background: #fff; border-radius: 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.08); padding: 0 0 24px 0;">
        <!-- Header -->
        <div style="background-color: #51bde5; padding: 24px 24px 20px 24px; text-align: center; border-radius: 10px 10px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 26px;">${encabezado}</h1>
          <p style="color: white; margin: 8px 0 0 0; font-size: 15px;">${bajada}</p>
        </div>

        <!-- Bloque Cita -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.apptDetails}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.apptDate}</strong></td><td style="padding: 7px 0;">${appointmentDate}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.apptTime}</strong></td><td style="padding: 7px 0;">${appointmentTime}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.apptBranch}</strong></td><td style="padding: 7px 0;">${escapeHtml(dealershipAddress)}</td></tr>
          </table>
        </div>

        <!-- Bloque Cliente -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.clientInfo}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.name}</strong></td><td style="padding: 7px 0;">${escapeHtml(customerName)}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.email}</strong></td><td style="padding: 7px 0;"><a href="mailto:${escapeHtml(customerEmail)}" style="color: #51bde5; text-decoration: none;">${escapeHtml(customerEmail)}</a></td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.phone}</strong></td><td style="padding: 7px 0;"><a href="tel:${escapeHtml(customerPhone)}" style="color: #51bde5; text-decoration: none;">${escapeHtml(customerPhone)}</a></td></tr>
          </table>
        </div>

        <!-- Bloque Vehículo -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.apptVehicle}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.brand}</strong></td><td style="padding: 7px 0;">${escapeHtml(vehicleDetails.brand)}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.model}</strong></td><td style="padding: 7px 0;">${escapeHtml(vehicleDetails.model)}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.year}</strong></td><td style="padding: 7px 0;">${escapeHtml(vehicleDetails.year)}</td></tr>
          </table>
        </div>

        ${
          notes
            ? `<div style="padding: 24px 24px 0 24px;"><h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.apptNotes}</h2><div style="padding: 10px 0; white-space: pre-wrap; font-size: 15px;">${escapeHtml(notes)}</div></div>`
            : ''
        }

        <!-- CTA Button -->
        <div style="margin: 30px 0 0 0; text-align: center;">
          <a href="https://portal.goauto.cl/appointments"
             style="background-color: #51bde5; color: white; padding: 12px 25px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block;">
            ${L.cta}
          </a>
        </div>

        <!-- Footer -->
        <div style="background-color: #f1f1f1; padding: 15px; text-align: center; font-size: 13px; color: #666; border-top: 1px solid #ddd; margin-top: 32px; border-radius: 0 0 10px 10px;">
          <p style="margin: 0;">
            ${L.footer1} <a href="https://goauto.cl" style="color: #51bde5; text-decoration: none;">GoAuto</a>.
            <br>${L.footer2}
          </p>
        </div>
      </div>
    </body>
    </html>
  `;
}

/**
 * Crea una plantilla HTML para cancelación de cita
 */
export function createAppointmentCancellationEmailTemplate({
  customerName,
  customerEmail,
  customerPhone,
  appointmentDate,
  appointmentTime,
  dealershipAddress,
  vehicleDetails,
  cancellationReason,
  country,
}: {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  appointmentDate: string;
  appointmentTime: string;
  dealershipAddress: string;
  vehicleDetails: {
    brand: string;
    model: string;
    year: string;
  };
  cancellationReason?: string;
  /** País del tenant (clients.country). 'BR' → correo en portugués; el resto, el de siempre. */
  country?: string | null;
}): string {
  const L = txtCorreo(country);
  return `
    <!DOCTYPE html>
    <html lang="${L.lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${L.cancelTitle}</title>
    </head>
    <body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 650px; margin: 0 auto; padding: 20px; color: #333; background: #f8f9fa;">
      <div style="background: #fff; border-radius: 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.08); padding: 0 0 24px 0;">
        <!-- Header -->
        <div style="background-color: #ef4444; padding: 24px 24px 20px 24px; text-align: center; border-radius: 10px 10px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 26px;">${L.cancelH1}</h1>
          <p style="color: white; margin: 8px 0 0 0; font-size: 15px;">${L.cancelSubtitle}</p>
        </div>

        <!-- Bloque Cita -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.cancelDetails}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.apptDate}</strong></td><td style="padding: 7px 0;">${appointmentDate}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.apptTime}</strong></td><td style="padding: 7px 0;">${appointmentTime}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.apptBranch}</strong></td><td style="padding: 7px 0;">${escapeHtml(dealershipAddress)}</td></tr>
          </table>
        </div>

        <!-- Bloque Cliente -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.clientInfo}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.name}</strong></td><td style="padding: 7px 0;">${escapeHtml(customerName)}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.email}</strong></td><td style="padding: 7px 0;"><a href="mailto:${escapeHtml(customerEmail)}" style="color: #51bde5; text-decoration: none;">${escapeHtml(customerEmail)}</a></td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.phone}</strong></td><td style="padding: 7px 0;"><a href="tel:${escapeHtml(customerPhone)}" style="color: #51bde5; text-decoration: none;">${escapeHtml(customerPhone)}</a></td></tr>
          </table>
        </div>

        <!-- Bloque Vehículo -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.cancelVehicle}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.brand}</strong></td><td style="padding: 7px 0;">${escapeHtml(vehicleDetails.brand)}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.model}</strong></td><td style="padding: 7px 0;">${escapeHtml(vehicleDetails.model)}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.year}</strong></td><td style="padding: 7px 0;">${escapeHtml(vehicleDetails.year)}</td></tr>
          </table>
        </div>

        ${
          cancellationReason
            ? `<div style="padding: 24px 24px 0 24px;"><h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.cancelReason}</h2><div style="padding: 10px 0; white-space: pre-wrap; font-size: 15px;">${escapeHtml(cancellationReason)}</div></div>`
            : ''
        }

        <!-- Footer -->
        <div style="background-color: #f1f1f1; padding: 15px; text-align: center; font-size: 13px; color: #666; border-top: 1px solid #ddd; margin-top: 32px; border-radius: 0 0 10px 10px;">
          <p style="margin: 0;">
            ${L.footer1} <a href="https://goauto.cl" style="color: #51bde5; text-decoration: none;">GoAuto</a>.
            <br>${L.footer2}
          </p>
        </div>
      </div>
    </body>
    </html>
  `;
}

/**
 * Crea una plantilla HTML para leads de vehículos
 */
export function createVehicleLeadEmailTemplate({
  leadType,
  customerName,
  customerEmail,
  customerPhone,
  vehicleDetails,
  additionalMessage,
  currencySymbol = '$',
  country,
}: {
  leadType: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  vehicleDetails: {
    brand: string;
    model: string;
    year: string;
    mileage?: string;
    condition?: string;
    price?: string;
    license_plate?: string;
  };
  additionalMessage?: string;
  /** Símbolo de la moneda del tenant ('$' CLP/USD, 'R$' BRL). */
  currencySymbol?: string;
  /** País del tenant (clients.country). 'BR' → correo en portugués; el resto, el de siempre. */
  country?: string | null;
}): string {
  const L = txtCorreo(country);
  const leadTypeMap: Record<string, string> = L.leadTypes;

  const leadTypeName = leadTypeMap[leadType] || leadType;

  // (Para formularios configurables desde el builder, ver createDynamicLeadEmailTemplate al final.)

  // Formatear los valores del vehículo para mejor visualización
  const formattedVehicleDetails = {
    brand: vehicleDetails.brand || L.notSpecified,
    model: vehicleDetails.model || L.notSpecified,
    year: vehicleDetails.year || L.notSpecified,
    mileage: vehicleDetails.mileage
      ? vehicleDetails.mileage.includes('km')
        ? vehicleDetails.mileage
        : vehicleDetails.mileage.includes('Máximo')
        ? `${vehicleDetails.mileage} km`
        : sinUnidadUsa(vehicleDetails.mileage, country)
        ? vehicleDetails.mileage
        : `${vehicleDetails.mileage} km`
      : L.notSpecified,
    condition: vehicleDetails.condition || L.notSpecified,
    // Solo se antepone el símbolo a montos crudos ('1.234.567'); textos ya
    // decorados ('Máximo $X', 'No especificado') pasan tal cual — el legacy les
    // pegaba un '$' extra ('$Máximo $X', '$No especificado').
    price: vehicleDetails.price
      ? /^\d/.test(vehicleDetails.price)
        ? `${currencySymbol}${vehicleDetails.price}`
        : vehicleDetails.price
      : L.notSpecified,
    license_plate: vehicleDetails.license_plate || L.notSpecified,
  };

  return `
    <!DOCTYPE html>
    <html lang="${L.lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${L.leadTitle} ${leadTypeName}</title>
    </head>
    <body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 650px; margin: 0 auto; padding: 20px; color: #333; background: #f8f9fa;">
      <div style="background: #fff; border-radius: 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.08); padding: 0 0 24px 0;">
        <!-- Header -->
        <div style="background-color: #51bde5; padding: 24px 24px 20px 24px; text-align: center; border-radius: 10px 10px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 26px;">${L.leadH1} ${leadTypeName}</h1>
        </div>
        <!-- Bloque Cliente -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.clientInfo}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.name}</strong></td><td style="padding: 7px 0;">${customerName}</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.email}</strong></td><td style="padding: 7px 0;"><a href="mailto:${customerEmail}" style="color: #51bde5; text-decoration: none;">${customerEmail}</a></td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.phone}</strong></td><td style="padding: 7px 0;"><a href="tel:${customerPhone}" style="color: #51bde5; text-decoration: none;">${customerPhone}</a></td></tr>
          </table>
        </div>
        <!-- Bloque Vehículo -->
        <div style="padding: 24px 24px 0 24px;">
          <h2 style="color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;">${L.vehicleDetails}</h2>
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            <tr><td style="padding: 7px 0; width: 180px;"><strong>${L.brand}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.brand
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.model}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.model
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.year}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.year
            }</td></tr>
            <tr><td style="padding: 7px 0;"><strong>${L.mileage}</strong></td><td style="padding: 7px 0;">${
              formattedVehicleDetails.mileage
            }</td></tr>
            ${
              leadType !== 'search-request'
                ? `<tr><td style="padding: 7px 0;"><strong>${L.condition}</strong></td><td style="padding: 7px 0;">${formattedVehicleDetails.condition}</td></tr>`
                : ''
            }
            ${
              leadType === 'buy-consignment'
                ? ''
                : `<tr><td style="padding: 7px 0;"><strong>${L.price}</strong></td><td style="padding: 7px 0;">${formattedVehicleDetails.price}</td></tr>`
            }
          </table>
        </div>
        <!-- Mensaje del cliente -->
        ${
          additionalMessage
            ? `<div style=\"padding: 24px 24px 0 24px;\"><h2 style=\"color: #222; font-size: 18px; margin: 0 0 18px 0; letter-spacing: 1px;\">${L.customerMessage}</h2><div style=\"padding: 10px 0; white-space: pre-wrap; font-size: 15px;\">${additionalMessage}</div></div>`
            : ''
        }
        <!-- CTA Button -->
        <div style="margin: 30px 0 0 0; text-align: center;">
          <a href="https://portal.goauto.cl/leads" 
             style="background-color: #51bde5; color: white; padding: 12px 25px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block;">
            ${L.cta}
          </a>
        </div>
        <!-- Footer -->
        <div style="background-color: #f1f1f1; padding: 15px; text-align: center; font-size: 13px; color: #666; border-top: 1px solid #ddd; margin-top: 32px; border-radius: 0 0 10px 10px;">
          <p style="margin: 0;">
            ${L.footer1} <a href="https://goauto.cl" style="color: #51bde5; text-decoration: none;">GoAuto</a>.
            <br>${L.footer2}
          </p>
        </div>
      </div>
    </body>
    </html>
  `;
}

/**
 * Escapa HTML para evitar inyección al volcar valores arbitrarios del usuario en el email.
 */
function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Plantilla HTML genérica para formularios configurables desde el builder.
 * Recibe una lista de campos { label, value } y los renderiza como tabla.
 * Los campos con isHeading se muestran como subtítulo de sección.
 * Todos los valores se escapan (anti-XSS).
 */
export function createDynamicLeadEmailTemplate({
  leadTypeName: leadTypeNameParam,
  fields,
  country,
}: {
  leadTypeName?: string;
  fields: { label: string; value?: string; isHeading?: boolean }[];
  /** País del tenant (clients.country). 'BR' → correo en portugués; el resto, el de siempre. */
  country?: string | null;
}): string {
  const L = txtCorreo(country);
  // Mismo default que antes ('Nueva solicitud'): sólo cuando no viene.
  const leadTypeName =
    leadTypeNameParam === undefined ? L.newRequest : leadTypeNameParam;
  const rows = (fields || [])
    .map((f) => {
      if (f.isHeading) {
        return `<tr><td colspan="2" style="padding: 20px 0 8px 0;"><h2 style="color: #222; font-size: 16px; margin: 0; letter-spacing: 1px;">${escapeHtml(
          f.label
        )}</h2></td></tr>`;
      }
      const rawValue = (f.value ?? '').toString().trim();
      const value = rawValue ? escapeHtml(rawValue).replace(/\n/g, '<br>') : L.notSpecified;
      return `<tr><td style="padding: 7px 0; width: 200px; vertical-align: top;"><strong>${escapeHtml(
        f.label
      )}:</strong></td><td style="padding: 7px 0;">${value}</td></tr>`;
    })
    .join('');

  return `
    <!DOCTYPE html>
    <html lang="${L.lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${escapeHtml(leadTypeName)}</title>
    </head>
    <body style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 650px; margin: 0 auto; padding: 20px; color: #333; background: #f8f9fa;">
      <div style="background: #fff; border-radius: 10px; box-shadow: 0 4px 6px rgba(0,0,0,0.08); padding: 0 0 24px 0;">
        <div style="background-color: #51bde5; padding: 24px 24px 20px 24px; text-align: center; border-radius: 10px 10px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 26px;">${escapeHtml(leadTypeName)}</h1>
        </div>
        <div style="padding: 24px 24px 0 24px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 15px;">
            ${rows}
          </table>
        </div>
        <div style="margin: 30px 0 0 0; text-align: center;">
          <a href="https://portal.goauto.cl/leads"
             style="background-color: #51bde5; color: white; padding: 12px 25px; text-decoration: none; border-radius: 4px; font-weight: bold; display: inline-block;">
            ${L.cta}
          </a>
        </div>
        <div style="background-color: #f1f1f1; padding: 15px; text-align: center; font-size: 13px; color: #666; border-top: 1px solid #ddd; margin-top: 32px; border-radius: 0 0 10px 10px;">
          <p style="margin: 0;">
            ${L.footer1} <a href="https://goauto.cl" style="color: #51bde5; text-decoration: none;">GoAuto</a>.
            <br>${L.footer2}
          </p>
        </div>
      </div>
    </body>
    </html>
  `;
}
